import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import EmailNav from "./EmailNav";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Send, Edit, Trash2, MailOpen, MousePointerClick, UserMinus, AlertCircle, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";
import { formatET } from "@/lib/dates";

const STATUS_BADGE = {
  draft: "bg-muted text-foreground",
  scheduled: "bg-amber-500/15 text-amber-500",
  sending: "bg-blue-500/15 text-blue-500",
  sent: "bg-emerald-500/15 text-emerald-500",
};

const Stat = ({ label, value, sub, icon: Icon, accent }) => (
  <Card data-testid={`detail-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent || "bg-primary/10 text-primary"}`}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold leading-tight">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
      </div>
    </CardContent>
  </Card>
);

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/email/campaigns/${id}/stats`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const cancelSchedule = async () => {
    try { await api.post(`/email/campaigns/${id}/cancel-schedule`); toast.success("Schedule cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this campaign?")) return;
    try { await api.delete(`/email/campaigns/${id}`); toast.success("Deleted"); navigate("/email/campaigns"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) {
    return <div data-testid="campaign-detail-loading"><EmailNav /><Skeleton className="h-40 mb-3" /><div className="grid grid-cols-2 md:grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div></div>;
  }
  if (!data?.campaign) return null;

  const c = data.campaign;
  const stats = c.stats || {};
  const openRate = stats.delivered ? Math.round((stats.unique_opens / stats.delivered) * 100) : 0;
  const clickRate = stats.delivered ? Math.round((stats.unique_clicks / stats.delivered) * 100) : 0;

  return (
    <div data-testid="campaign-detail-page">
      <EmailNav />
      <Button variant="ghost" size="sm" onClick={() => navigate("/email/campaigns")} className="mb-3"><ArrowLeft size={14} className="mr-2" /> Back to campaigns</Button>

      <Card className="mb-4">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Badge className={STATUS_BADGE[c.status] || "bg-muted"}>{c.status}</Badge>
              <h1 className="text-2xl font-outfit font-semibold mt-2">{c.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">{c.subject}</p>
              {c.preview_text && <p className="text-xs text-muted-foreground italic">{c.preview_text}</p>}
              <div className="text-xs text-muted-foreground mt-2 flex gap-3 flex-wrap">
                <span>Brand: <span className="capitalize">{c.brand}</span></span>
                {c.scheduled_at && <span><CalIcon size={11} className="inline mr-1" />Scheduled: {formatET(c.scheduled_at)}</span>}
                {c.sent_at && <span><Send size={11} className="inline mr-1" />Sent: {formatET(c.sent_at)}</span>}
                {c.created_at && <span>Created: {formatET(c.created_at)}</span>}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {c.status === "scheduled" && (
                <Button variant="outline" size="sm" onClick={cancelSchedule} data-testid="cancel-schedule-btn">Cancel schedule</Button>
              )}
              {c.status === "draft" && (
                <Button size="sm" onClick={() => navigate(`/email/campaigns/${id}/edit`)} data-testid="detail-edit-btn">
                  <Edit size={13} className="mr-2" /> Edit
                </Button>
              )}
              {c.status !== "sending" && (
                <Button variant="ghost" size="icon" onClick={remove} data-testid="detail-delete-btn">
                  <Trash2 size={14} className="text-rose-500" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Sent" value={stats.sent || 0} sub={`${stats.delivered || 0} delivered`} icon={Send} accent="bg-primary/10 text-primary" />
        <Stat label="Opens" value={stats.unique_opens || 0} sub={`${openRate}% open rate · ${stats.total_opens || 0} total`} icon={MailOpen} accent="bg-emerald-500/10 text-emerald-500" />
        <Stat label="Clicks" value={stats.unique_clicks || 0} sub={`${clickRate}% click rate · ${stats.total_clicks || 0} total`} icon={MousePointerClick} accent="bg-amber-500/10 text-amber-500" />
        <Stat label="Bounced" value={stats.bounces || 0} sub={`${stats.unsubscribes || 0} unsubscribed`} icon={AlertCircle} accent="bg-rose-500/10 text-rose-500" />
      </div>

      {/* Top clicked links */}
      {(data.top_links || []).length > 0 && (
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-base font-outfit flex items-center gap-2"><MousePointerClick size={16} /> Top clicked links</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.top_links.map((l) => (
              <div key={l.url} className="flex items-center justify-between gap-3 p-2 rounded border border-border">
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline truncate flex-1">{l.url}</a>
                <span className="text-xs text-muted-foreground shrink-0">{l.unique_clicks} unique · {l.clicks} total</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recent events */}
      <Card>
        <CardHeader><CardTitle className="text-base font-outfit">Recent activity</CardTitle></CardHeader>
        <CardContent>
          {(data.recent_events || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No activity yet.</p>
          ) : (
            <div className="space-y-1">
              {data.recent_events.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-xs py-1 border-b border-border/50 last:border-0">
                  <Badge variant="outline" className={`capitalize text-[10px] ${
                    e.event_type === "open" ? "text-emerald-500 border-emerald-500/30" :
                    e.event_type === "click" ? "text-amber-500 border-amber-500/30" :
                    e.event_type === "bounce" ? "text-rose-500 border-rose-500/30" :
                    e.event_type === "sent" ? "text-blue-500 border-blue-500/30" : ""
                  }`}>{e.event_type}</Badge>
                  <span className="flex-1 truncate text-foreground">{e.contact_email}</span>
                  {e.url && <span className="text-muted-foreground truncate max-w-xs hidden md:inline">→ {e.url}</span>}
                  <span className="text-muted-foreground">{formatET(e.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Body preview */}
      <Card className="mt-4">
        <CardHeader><CardTitle className="text-base font-outfit">Email content</CardTitle></CardHeader>
        <CardContent>
          <div className="border border-border rounded p-3 bg-white text-black max-h-[500px] overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: (c.body_html || "").replace(/\{\{first_name\}\}/g, "Jamie") }} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
